<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { brand, brandConfig } from '@/config/brands'

const props = defineProps<{
  clientId?: string
  slotId?: string
}>()

const ADSENSE_CLIENT_ID = props.clientId
  || import.meta.env.VITE_ADSENSE_CLIENT_ID
  || brandConfig.texts?.adsenseClientId
  || ''
const ADSENSE_SLOT_ID = props.slotId
  || import.meta.env.VITE_ADSENSE_SLOT_ID
  || brandConfig.texts?.adsenseSlotId
  || ''

const scriptLoaded = ref(false)

function loadAdsenseScript() {
  // eslint-disable-next-line no-console
  console.log('[AdSense] script not found in DOM, loading dynamically...')
  const script = document.createElement('script')
  script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js'
  script.async = true
  script.crossOrigin = 'anonymous'
  script.onload = () => {
    // eslint-disable-next-line no-console
    console.log('[AdSense] script loaded, pushing ad...')
    scriptLoaded.value = true
    pushAd()
  }
  script.onerror = () => {
    console.error('[AdSense] script load failed')
  }
  document.head.appendChild(script)
}

function pushAd() {
  try {
    (window.adsbygoogle = window.adsbygoogle || []).push({})
    // eslint-disable-next-line no-console
    console.log('[AdSense] push success')
  }
  catch (e) {
    console.error('[AdSense] push error:', e)
  }
}

onMounted(() => {
  if (brand !== 'rocnovel') {
    // eslint-disable-next-line no-console
    console.log('[AdSense] skip: brand is not rocnovel')
    return
  }
  if (!ADSENSE_CLIENT_ID || !ADSENSE_SLOT_ID) {
    // eslint-disable-next-line no-console
    console.log('[AdSense] skip: missing clientId or slotId')
    return
  }

  // eslint-disable-next-line no-console
  console.log('[AdSense] mounted, clientId=', ADSENSE_CLIENT_ID, 'slotId=', ADSENSE_SLOT_ID)

  const existingScript = document.querySelector('script[src*="adsbygoogle.js"]')
  if (existingScript) {
    // eslint-disable-next-line no-console
    console.log('[AdSense] script already exists in DOM')
    scriptLoaded.value = true
    pushAd()
  }
  else {
    loadAdsenseScript()
  }
})
</script>

<template>
  <div v-if="brand === 'rocnovel' && ADSENSE_CLIENT_ID && ADSENSE_SLOT_ID" class="adsense-inline-article">
    <p class="adsense-label">
      Ad
    </p>
    <ins
      class="adsbygoogle"
      style="display:block; text-align:center;"
      data-ad-layout="in-article"
      data-ad-format="fluid"
      :data-ad-client="ADSENSE_CLIENT_ID"
      :data-ad-slot="ADSENSE_SLOT_ID"
    />
  </div>
</template>

<style lang="scss" scoped>
.adsense-inline-article {
  margin: 24px 0;
  min-height: 120px;
  clear: both;
  background: #f5f5f5;
  border-radius: 8px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.adsense-label {
  font-size: 10px;
  color: #999;
  text-align: center;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.adsense-inline-article :deep(ins.adsbygoogle[data-ad-status="filled"]) {
  display: block !important;
}
</style>
